import React, { useEffect, useState } from "react";
import { z } from "zod";
import apiClient from "@/services";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { db } from "@/utils/firebase";
import { collection, query, getDocs, where } from "firebase/firestore";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import WhatsAppButton from "@/components/WhatsAppButton";

const formSchema = z.object({
  first_name: z
    .string()
    .min(1, "Name is required")
    .max(25, "Name cannot exceed 20 characters"),
  last_name: z
    .string()
    .min(1, "Name is required")
    .max(25, "Name cannot exceed 20 characters"),
  email: z.string().min(1, "Email is required").email("Invalid email format"),
  password: z
    .string()
    .min(8, "Password should be atleast 8 characters")
    .max(15, "Name cannot exceed 15 characters"),
  phone: z
    .string()
    .min(
      10,
      "Phone number must be at least 10 digits, including the country code"
    )
    .max(15, "Phone number cannot exceed 15 digits")
    .regex(
      /^\+[1-9]\d{9,14}$/,
      "Phone number must start with '+' followed by a valid country code and digits"
    ),
  role: z.string().min(1, "Role is required"), // Expecting a single string now
});

function DeleteAlert({ setVisible, email, deleteAccount }) {
  return (
    <AlertDialog defaultOpen={true}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Action</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the agent account {email}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="secondary" onClick={() => setVisible(false)}>
            Cancel
          </Button>
          <Button
            onClick={deleteAccount}
            className="bg-red-600 hover:bg-red-600"
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Agents() {
  const [agents, setAgents] = useState();
  const [mode, setMode] = useState("agents");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  // Transfer Account
  const [newEmail, setNewEmail] = useState("");
  const [selectedAgent, setSelectedAgent] = useState();

  // Delete Account
  const [accountToDeleteUid, setAccountToDeleteUid] = useState("");
  const [accountToDeleteEmail, setAccountToDeleteEmail] = useState("");

  // Filters
  const [filters, setFilters] = useState({
    role: "all",
    search: "",
  });
  const [filteredList, setFilteredList] = useState([]);

  const roleMap = {
    "Buyer Agent": ["buyer_agent"],
    "Seller Agent": ["seller_agent"],
    "Buyer Agent & Seller Agent": ["buyer_agent", "seller_agent"],
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      password: "",
      role: "", // Default to an empty string
    },
  });

  // Define columns for the table
  const columns = [
    {
      accessorKey: "sno",
      header: "Sno",
      cell: (info) => info.row.index + 1, // Automatically generate Sno
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: (info) =>
        `${info.row.original.first_name} ${info.row.original.last_name}`,
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phone",
      header: "Phone",
    },
    {
      accessorKey: "roles",
      header: "Roles",
      cell: (info) => formatRoles(info.getValue()),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div className="flex gap-4">
          <WhatsAppButton phone={info.row.original.phone} />

          <Button
            className="px-6 rounded-md"
            onClick={() => handleAction(info.row.original.email)}
          >
            Transfer Account
          </Button>

          <Button
            className="px-6 rounded-md bg-red-600 hover:bg-red-600"
            onClick={() =>
              handleDelete(info.row.original.email, info.row.original.uid)
            }
          >
            Delete Account
          </Button>
        </div>
      ),
    },
  ];

  // Initialize the table
  const table = useReactTable({
    columns,
    data: filteredList,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10, // Default page size
      },
    },
  });

  // Create Agent Account
  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const { status } = await apiClient.post("/createAgent", {
        ...values,
        roles: roleMap[values.role],
      });
      if (status === 201) {
        // Reset form data
        form.reset();
        alert("Agent account created successfully");
      }
    } catch (err) {
      alert("Error creating agent account");
      console.log("Error creating agent account", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Agent Change
  const handleAgentChange = (value) => {
    // Find the selected agent object from the agents array
    const selectedVal = agents.find((agent) => agent.email === value);
    // Set the full agent object in state
    setSelectedAgent(selectedVal);
  };

  // Format Roles
  function formatRoles(roles) {
    if (!Array.isArray(roles)) return "";

    return roles
      .map((role) => {
        if (!role) return "";

        // Split by underscore
        const words = role.split("_");

        // Capitalize first letter of each word and join with space
        return words
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      })
      .filter(Boolean) // Remove any empty strings
      .join(", "); // Join with comma and space
  }

  // Format Roles
  function formatRole(role) {
    if (!role) return "";

    // Split by underscore
    const words = role.split("_");

    // Capitalize first letter of each word and join with space
    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Update filters when inputs change
  const handleRoleChange = (value) => {
    setFilters((prev) => ({ ...prev, role: value }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleAction = (email) => {
    setMode("transfer");
    handleAgentChange(email);
  };

  // Deletion Alert
  const handleDelete = (email, uid) => {
    setVisible(true);
    setAccountToDeleteUid(uid);
    setAccountToDeleteEmail(email);
  };

  // Delete Account
  const deleteAccount = async () => {
    if (!accountToDeleteUid) {
      alert("Please select an account to delete!");
      return;
    }

    try {
      setLoading(true);
      const { status } = await apiClient.post("/deleteAgent", {
        accountToDeleteUid,
      });
      if (status === 200) {
        alert("Account deleted sucessfully");
        window.location.reload();
      }
    } catch (err) {
      alert("Failed to delete the account");
      console.log("Error deleting agent account", err);
    } finally {
      setLoading(false);
    }
  };

  // Transfer Agent Account
  const onTransfer = async () => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!selectedAgent) {
      alert("Please select an agent to transfer to");
      return;
    }
    if (!newEmail) {
      alert("Please enter the new email address");
      return;
    }
    if (!emailRegex.test(newEmail)) {
      alert("Please enter a valid email address");
      return;
    }
    if (!emailRegex.test(selectedAgent.email)) {
      alert("Selected agent's email is invalid. Please select another agent");
      return;
    }

    try {
      setLoading(true);
      const { status } = await apiClient.post("/transferAgent", {
        newEmail,
        userId: selectedAgent.uid,
      });
      if (status === 201) {
        alert("Account transferred sucessfully");
        window.location.reload();
      }
    } catch (err) {
      alert("Failed to transfer the account");
      console.log("Error transferring agent account", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters whenever filters state changes
  const filterProducts = () => {
    const filtered = agents.filter((agent) => {
      // Filter by role
      const roleMatch =
        filters.role === "all" ||
        (Array.isArray(agent.roles) && agent.roles.includes(filters.role));

      // Filter by search term (name, email, phone)
      const searchTerm = filters.search.toLowerCase();
      const searchMatch =
        !filters.search ||
        `${agent.first_name} ${agent.last_name}`
          .toLowerCase()
          .includes(searchTerm) ||
        agent.email?.toLowerCase().includes(searchTerm) ||
        agent.phone?.toLowerCase().includes(searchTerm);

      return roleMatch && searchMatch;
    });

    setFilteredList(filtered);
  };

  // Fetch Agents
  useEffect(() => {
    const getAgents = async () => {
      try {
        // Reference to users collection
        const usersRef = collection(db, "users");

        // Create query for users with either role
        const agentQuery = query(
          usersRef,
          where("roles", "array-contains-any", ["buyer_agent", "seller_agent"])
        );
        // Execute query
        const querySnapshot = await getDocs(agentQuery);

        // Map results to array of user data
        const agentList = querySnapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        }));

        setAgents(agentList);
        setFilteredList(agentList);
      } catch (error) {
        console.error("Error fetching agent users:", error);
        throw error;
      }
    };

    getAgents();
  }, []);

  // Apply filters whenever filters state changes
  useEffect(() => {
    // Make sure agents is defined before filtering
    if (!Array.isArray(agents)) return;

    filterProducts();
  }, [filters]); // Add filters as a dependency

  if (loading || !agents) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper">
      <div className="flex gap-5">
        <div className="cursor-pointer" onClick={() => setMode("agents")}>
          <h1 className="text-sm font-bold text-green ml-0.5 !w-[106px]">
            Manage Agents
          </h1>
          {mode === "agents" && <div className="highlight !w-[106px]" />}
        </div>

        <div className="cursor-pointer" onClick={() => setMode("create")}>
          <h1 className="text-sm font-bold text-green ml-0.5 !w-[106px]">
            Create Account
          </h1>
          {mode === "create" && <div className="highlight !w-[106px]" />}
        </div>

        <div className="cursor-pointer" onClick={() => setMode("transfer")}>
          <h1 className="text-sm font-bold text-green ml-0.5">
            Transfer Account
          </h1>
          {mode === "transfer" && <div className="highlight !w-[120px]" />}
        </div>
      </div>

      {/*  Alert  */}
      {visible && accountToDeleteEmail && (
        <DeleteAlert
          setVisible={setVisible}
          email={accountToDeleteEmail}
          deleteAccount={deleteAccount}
        />
      )}

      {mode === "agents" && (
        <>
          <div className="flex flex-col md:flex-row gap-4 my-4">
            <div className="md:w-1/3">
              <label className="block text-sm font-medium mb-1">
                Agent Roles
              </label>
              <Select onValueChange={handleRoleChange} value={filters.role}>
                <SelectTrigger className="w-full !h-[40px]">
                  <SelectValue placeholder="Select Role">
                    {filters.role === "all"
                      ? "All Roles"
                      : formatRole(filters.role)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {["buyer_agent", "seller_agent"].map((role, i) => (
                    <SelectItem key={i} value={role}>
                      {formatRole(role)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:w-2/3">
              <label className="block text-sm font-medium mb-1">Search</label>
              <Input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={filters.search}
                onChange={handleSearchChange}
                className="w-full"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm">
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </span>
              <Select
                value={table.getState().pagination.pageSize.toString()}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger className="w-[80px]">
                  <span className="text-sm">
                    {table.getState().pagination.pageSize}
                  </span>
                </SelectTrigger>
                <SelectContent className="!w-[80px]">
                  {[10, 25, 50].map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      Show {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}
      {mode === "create" && (
        <>
          <p className="mt-3 ml-3 text-sm text-muted mobileMargin">
            Create a Buyer or Seller agent account. Fill the form to create an
            agent account.
          </p>

          <Form {...form}>
            <form
              className="space-y-4 p-3"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Phone{" "}
                          <span className="text-muted">
                            (with country code)*
                          </span>
                        </FormLabel>

                        <FormControl>
                          <Input placeholder="Enter phone" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-between mobileFormWrapper">
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password*</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Role */}
                <div className="w-[49%] mobileInputWrapper">
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role*</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(value)}
                          defaultValue={field.value}
                        >
                          <FormControl className="h-10">
                            <SelectTrigger>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[
                              "Buyer Agent",
                              "Seller Agent",
                              "Buyer Agent & Seller Agent",
                            ].map((role) => (
                              <SelectItem key={role} value={role}>
                                {role}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="text-right">
                <Button
                  type="submit"
                  className="w-[200px] bg-green mobileInputWrapper mt-2"
                >
                  Submit
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
      {mode === "transfer" && (
        <>
          <p className="mt-3 ml-3 text-sm text-muted mobileMargin">
            Transfer an agent account to a new agent.
          </p>

          <div className="w-[49%] py-2 md:p-3 mobileInputWrapper">
            <p className="ml-0.5 text-sm">Current Agent's Email*</p>

            <Select onValueChange={handleAgentChange}>
              <SelectTrigger className="h-10 mt-2">
                <SelectValue placeholder="Select current agent's account" />
              </SelectTrigger>

              <SelectContent>
                {agents.map((agent, i) => (
                  <SelectItem key={i} value={agent.email}>
                    {agent.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedAgent && (
            <div className="w-[49%] py-2 md:p-3 md:py-2 mobileInputWrapper">
              <p className="ml-0.5 mb-2 text-sm">ACCOUNT DETAILS</p>
              <div className="p-3 bg-white rounded-md">
                <p className="ml-0.5 text-sm">
                  Name: {selectedAgent?.first_name} {selectedAgent?.last_name}
                </p>
                <p className="ml-0.5 mt-1 text-sm">
                  Email: {selectedAgent?.email}
                </p>
                <p className="ml-0.5 mt-1 text-sm">
                  Phone: {selectedAgent?.phone || "N/A"}
                </p>
                <p className="ml-0.5 mt-1 text-sm">
                  Roles: {formatRoles(selectedAgent?.roles)}
                </p>
                <p className="ml-0.5 mt-1 text-sm">
                  Company: {selectedAgent?.company || "N/A"}
                </p>
              </div>
            </div>
          )}

          <div className="w-[49%] py-2 md:p-3 mobileInputWrapper">
            <p className="ml-0.5 text-sm">New Agent's Email*</p>
            <Input
              type="email"
              value={newEmail}
              placeholder="Enter new agent's email"
              onChange={(e) => setNewEmail(e.target.value)}
              className="mt-2"
            />
          </div>

          <div className="text-right">
            <Button
              onClick={onTransfer}
              className="w-[200px] bg-green mobileInputWrapper mt-2"
            >
              Transfer Account
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default Agents;
